/**
 * PickSheetPrint — /jobs/:jobId/pick-sheet (MATERIAL-REQ-01, Seth 2026-09-25).
 * One page for whoever stages a job: the equipment, the accessories, what the
 * office planned and every open request from the crew — a box to tick for
 * each, where it is, and what's still on order — plus a label to cut out and
 * tape on the staged pile: the customer's last name big, the day, the crew and
 * a QR code that opens the job's Warehouse tab in Pegasus.
 * Black and white, letter portrait. Reads the same records as the warehouse
 * screens and the crew's phone.
 */
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMemberName } from "@/components/TeamMemberSelect";
import { useJob, useJobEquipment, useJobMaterials, useJobVisits, type JobMaterial } from "./jobs";
import { useJobMaterialRequests, useQrSvg, lineStatusText, URGENCY_LABEL } from "./materialRequests";

const day = (iso: string | null | undefined) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");
const shortDay = (iso: string | null | undefined) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" }) : "");
const when = (iso: string) => new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
const qtyText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, "").replace(/\.$/, ""));

export function PickSheetPrintView() {
  const { jobId = "" } = useParams();
  const { data: job, isLoading } = useJob(jobId);
  const { data: equip } = useJobEquipment(jobId);
  const { data: mats } = useJobMaterials(jobId);
  const { data: reqs } = useJobMaterialRequests(jobId);
  const { data: visits } = useJobVisits(jobId);
  const name = useMemberName();
  const qrText = typeof window !== "undefined" && jobId ? `${window.location.origin}/jobs/${jobId}?tab=warehouse` : null;
  const { data: qr } = useQrSvg(qrText);

  const today = new Date().toLocaleDateString("en-CA");
  const visit = (visits ?? []).filter((v) => v.scheduled_date).sort((a, b) => (a.scheduled_date! < b.scheduled_date! ? -1 : 1))
    .find((v) => v.scheduled_date! >= today) ?? null;
  const crew = visit?.assigned_empno ? name(visit.assigned_empno) : null;
  const units = (equip ?? []).filter((e) => e.kind !== "accessory" && e.kind !== "material");
  const accessories = (equip ?? []).filter((e) => e.kind === "accessory");
  const planned = (mats ?? []).filter((m) => (m.source ?? "office") === "office");
  const requests = (reqs ?? []).filter((r) => r.status === "open")
    .map((r) => ({ ...r, lines: r.lines.filter((l) => l.pull_status !== "cancelled" && l.pull_status !== "picked_up") }))
    .filter((r) => r.lines.length);
  const needBy = requests.map((r) => r.need_by).filter(Boolean).sort()[0] ?? null;
  const itemCount = units.length + accessories.length + planned.length + requests.reduce((n, r) => n + r.lines.length, 0);
  const onOrder = requests.reduce((n, r) => n + r.lines.filter((l) => l.pull_status === "ordered").length, 0);
  const last = (job?.customer_name ?? "").trim().split(/\s+/).pop() ?? "";

  useEffect(() => { document.title = `Pick Sheet · ${job?.customer_name ?? "Job"}`; }, [job?.customer_name]);

  if (isLoading) return <p className="p-6 text-sm text-slate-500">Loading…</p>;
  if (!job) return <p className="p-6 text-sm text-slate-600">That job wasn&apos;t found.</p>;

  const Box = ({ on }: { on?: boolean }) => (
    <span className="inline-flex h-4 w-4 items-center justify-center border-[1.5px] border-black text-[11px] font-bold leading-none">{on ? "✓" : ""}</span>
  );
  const Head = ({ children }: { children: React.ReactNode }) => (
    <tr><td colSpan={4} className="border-t-2 border-black pb-0.5 pt-2 text-[11px] font-bold uppercase tracking-wide">{children}</td></tr>
  );
  const matRow = (m: JobMaterial, where: string, done: boolean) => (
    <tr key={m.id} className="border-t border-slate-300 align-top">
      <td className="w-7 py-1"><Box on={done} /></td>
      <td className="w-16 py-1 pr-2 font-semibold tabular-nums">{qtyText(Number(m.qty))}{m.unit && m.unit !== "ea" ? ` ${m.unit}` : ""}</td>
      <td className="py-1 pr-3">
        {m.description}
        {m.source === "request" && m.typed && m.typed.toLowerCase() !== m.description.toLowerCase() && <span className="block text-[10.5px] text-slate-600">They said: “{m.typed}”{m.item_id ? "" : " · not in the catalog"}</span>}
      </td>
      <td className="w-[32%] py-1">{where}</td>
    </tr>
  );

  return (
    <div className="mx-auto max-w-[8.5in] bg-white p-6 text-[12.5px] text-black print:max-w-none print:p-0" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
      <style>{`@media print { @page { size: letter portrait; margin: 0.45in; } html, body { background: #fff !important; } .no-print { display: none !important; } .keep { break-inside: avoid; page-break-inside: avoid; } }`}</style>
      <div className="no-print mb-4 flex items-center gap-2 rounded-xl border border-hairline bg-slate-50 px-3 py-2 text-[13px] text-slate-600">
        <span className="font-semibold text-slate-800">Pick sheet</span>
        <span className="text-slate-400">Tick each box as it goes on the pile; cut out the label and tape it on.</span>
        <button type="button" onClick={() => window.print()} className="ml-auto rounded-lg bg-slate-900 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-slate-800">Print</button>
      </div>

      <header className="border-b-2 border-black pb-1.5">
        <div className="text-[11px] font-bold uppercase tracking-wide">Pick Sheet · Sedgwick Heating Warehouse</div>
        <h1 className="text-[22px] font-bold leading-tight">{job.customer_name ?? job.custno}</h1>
        <p className="text-[12px]">{[job.title, job.address].filter(Boolean).join(" · ")}{job.source_dispatch ? ` · #${job.source_dispatch}` : ""}</p>
        <p className="mt-0.5 text-[12px]">
          <b>{visit ? "Install:" : "Not scheduled"}</b> {visit ? `${day(visit.scheduled_date)}${visit.scheduled_window ? ` · ${visit.scheduled_window}` : ""}` : ""}
          {crew ? <> · <b>Crew:</b> {crew}</> : null}
          {needBy ? <> · <b>Needed:</b> {day(needBy)}</> : null}
        </p>
      </header>

      <table className="mt-1 w-full border-collapse">
        <thead>
          <tr className="text-left text-[10.5px] uppercase tracking-wide text-slate-600">
            <th className="w-7 py-1 font-semibold">Got</th><th className="w-16 py-1 font-semibold">Qty</th><th className="py-1 font-semibold">Item</th><th className="py-1 font-semibold">Where</th>
          </tr>
        </thead>
        <tbody>
          {units.length > 0 && <Head>Equipment</Head>}
          {units.map((e) => (
            <tr key={e.id} className="border-t border-slate-300 align-top">
              <td className="py-1"><Box on={!!e.pulled_at} /></td>
              <td className="py-1 pr-2 font-semibold">{e.qty || 1}</td>
              <td className="py-1 pr-3">{[e.manufacturer, e.model].filter(Boolean).join(" ") || e.category} · {e.category}{e.serial ? ` · S/N ${e.serial}` : ""}</td>
              <td className="py-1">{e.pulled_at ? "Pulled" : ""}</td>
            </tr>
          ))}
          {accessories.length > 0 && <Head>Accessories</Head>}
          {accessories.map((e) => (
            <tr key={e.id} className="border-t border-slate-300 align-top">
              <td className="py-1"><Box on={!!e.pulled_at} /></td>
              <td className="py-1 pr-2 font-semibold">{e.qty || 1}</td>
              <td className="py-1 pr-3">{[e.manufacturer, e.model].filter(Boolean).join(" ") || e.category}</td>
              <td className="py-1">{e.pulled_at ? "Pulled" : ""}</td>
            </tr>
          ))}
          {planned.length > 0 && <Head>Materials the office planned</Head>}
          {planned.map((m) => matRow(m, m.shelf ?? "", m.pull_status === "ready"))}
          {requests.map((r) => (
            <FragmentRows key={r.id}>
              <Head>{(r.requested_by_name ?? "Crew").split(/\s+/)[0]}&apos;s request · {when(r.created_at)} · {URGENCY_LABEL[r.urgency]}</Head>
              {r.lines.map((l) => matRow(l, l.pull_status === "requested" ? "" : lineStatusText(l), l.pull_status === "ready"))}
              {r.note && <tr><td colSpan={4} className="py-1 text-[11.5px]"><b>From {(r.requested_by_name ?? "the crew").split(/\s+/)[0]}:</b> {r.note}</td></tr>}
            </FragmentRows>
          ))}
        </tbody>
      </table>

      <div className="mt-3 grid grid-cols-2 gap-6 text-[12px]">
        <div><b>Pulled by</b> <span className="inline-block min-w-[2in] border-b border-black">&nbsp;</span></div>
        <div><b>Picked up by</b> <span className="inline-block min-w-[2in] border-b border-black">&nbsp;</span></div>
      </div>

      <div className="keep mt-6 border-t border-dashed border-black pt-2">
        <p className="text-[10px] text-slate-600">✂ Cut out this label and tape it on the staged material</p>
        <div className="mt-1.5 flex items-center gap-4 border-2 border-black p-3">
          <div className="min-w-0 flex-1">
            <div className="text-[34px] font-extrabold uppercase leading-none tracking-tight">{last || job.customer_name}</div>
            <div className="mt-1 text-[14px] font-bold">{[shortDay(visit?.scheduled_date), crew, job.source_dispatch ? `#${job.source_dispatch}` : null].filter(Boolean).join(" · ")}</div>
            <div className="text-[12px]">{job.address}</div>
            <div className="mt-0.5 text-[12px]">{itemCount} item{itemCount === 1 ? "" : "s"}{onOrder ? ` · ${onOrder} still on order` : ""}</div>
          </div>
          <div className="h-[1.3in] w-[1.3in] shrink-0 [&>svg]:h-full [&>svg]:w-full" role="img" aria-label="QR code — opens this job in Pegasus"
            dangerouslySetInnerHTML={{ __html: qr ?? "" }} />
        </div>
        <p className="mt-1 text-[9.5px] text-slate-500">Scan the code to open this job in Pegasus. Printed {new Date().toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} — requests added after printing aren&apos;t on this sheet.</p>
      </div>
    </div>
  );
}

function FragmentRows({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
